import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import {
  DEFAULT_DASHBOARD_FROM_DATE,
  DEFAULT_DASHBOARD_TO_DATE,
  DrillLevel,
} from '../../dashboard-state.service';
import { environment } from '../../../environments/environment';

export interface KpiValue {
  key:    string;
  name:   string;
  value:  number | null;
  format: 'currency' | 'percent' | 'days' | 'number';
  note:   string | null;
  status?: 'positive' | 'warning' | 'critical' | 'neutral';
  trendDirection?: 'up' | 'down' | 'flat' | null;
  sourceState?: 'current' | 'stale' | 'unavailable' | 'loading';
}

export interface DashboardKpis {
  periodLabel: string | null;
  kpis:        KpiValue[];
  progressionOfFinancialMetrics?: ProgressionFinancialMetric[];
  marginComparison?: FinancialOverviewPoint[];
  operatingCostPercentOfRevenue?: FinancialOverviewPoint[];
  financialOverview?: FinancialOverviewPoint[];
  financialOverviewDrillDetail?: FinancialOverviewPoint[];
  placementRevenuePerMonth?: EvertimePlacementRevenueRow[];
  gpmPermVsTempByMonth?: EvertimeGpMarginTrendPoint[];
  agedDebtBuckets?: AgedDebtBucketItem[];
  clientChaseList?: CreditControlClientRow[];
  clientChaseListDrillDownDetail?: ClientChaseListDrillDownDetailRow[];
  revenueConcentration?: RevenueConcentrationRow[];
  top10ClientsByGrossProfit?: EvertimeGpRow[];
  clientPerformance?: EvertimeClientPerformanceRow[];
  cashCollectedLedgerDrillDown?: CashCollectedLedgerDrillDownRow[];
  agedDebtOver90DaysDrillDown?: AgedDebtOver90DaysDrillDownRow[];
  agedDebtBucketSummaryView?: AgedDebtBucketSummaryViewRow[];
  agedDebtBucketDetailView?: AgedDebtBucketDetailViewRow[];
  turnoverDrillDownSummary?: TurnoverDrillDownSummary;
  turnoverDrillDownDetail?: TurnoverDrillDownDetailRow[];
  costOfSalesDrillDownDetail?: CostOfSalesDrillDownDetailRow[];
  administrativeCostDrillDownSummary?: AdministrativeCostDrillDownSummaryRow[];
  administrativeCostDrillDownDetail?: AdministrativeCostDrillDownDetailRow[];
  financialOverviewDailyDrillDetail?: FinancialOverviewDailyDrillDetailRow[];
}

interface FinancialDashboardKpiCard {
  key: string;
  name: string;
  value: number | null;
}

interface FinancialDashboardKpiResponse {
  scope?: {
    startDate?: string;
    endDate?: string;
  };
  kpis?: FinancialDashboardKpiCard[];
  progressionOfFinancialMetrics?: ProgressionFinancialMetric[];
  marginComparison?: FinancialDashboardMarginComparisonRow[];
  operatingCostPercentOfRevenue?: FinancialDashboardOperatingCostRow[];
  financialOverview?: FinancialDashboardOverviewRow[];
  financialOverviewDrillDetail?: FinancialDashboardDrillDetailRow[];
  placementRevenuePerMonth?: FinancialDashboardPlacementRevenueRow[];
  gpmPermVsTempByMonth?: FinancialDashboardGpmRow[];
  agedDebtBuckets?: FinancialDashboardAgedDebtRow[];
  clientChaseList?: FinancialDashboardClientChaseRow[];
  clientChaseListDrillDownDetail?: FinancialDashboardClientChaseDrillDownRow[];
  revenueConcentration?: RevenueConcentrationRow[];
  top10ClientsByGrossProfit?: FinancialDashboardClientGrossProfitRow[];
  clientPerformance?: Record<string, unknown>[];
  cashCollectedLedgerDrillDown?: CashCollectedLedgerDrillDownRow[];
  agedDebtOver90DaysDrillDown?: AgedDebtOver90DaysDrillDownRow[];
  agedDebtBucketSummaryView?: AgedDebtBucketSummaryViewRow[];
  agedDebtBucketDetailView?: AgedDebtBucketDetailViewRow[];
  turnoverDrillDownSummary?: TurnoverDrillDownSummary;
  turnoverDrillDownDetail?: Record<string, unknown>[];
  costOfSalesDrillDownDetail?: Record<string, unknown>[];
  administrativeCostDrillDownSummary?: AdministrativeCostDrillDownSummaryRow[];
  administrativeCostDrillDownDetail?: Record<string, unknown>[];
  financialOverviewDailyDrillDetail?: Record<string, unknown>[];
}

export interface CashCollectedLedgerDrillDownRow {
  monthLabel: string;
  monthlyCashCollected: number;
  cumulativeCashCollected: number;
  monthlyCashCollectedPercent: number | null;
  cumulativeCashCollectedPercent: number | null;
  drillable?: boolean;
  transactions?: CashCollectedTransactionRow[];
}

/** One cash event (invoice payment or receive overpayment) shown in the monthly drill-down. */
export interface CashCollectedTransactionRow {
  date: string;
  source: string;
  documentNumber: string | null;
  client: string;
  reference: string | null;
  amount: number;
}

export interface CashCollectedKpiSummary {
  compId: number;
  startDate: string;
  endDate: string;
  cashCollected: number;
  invoicePayments: number;
  receiveOverpayments: number;
  invoicePaymentCount: number;
  receiveOverpaymentCount: number;
  totalCashEventCount: number;
}

export interface CashCollectedMonthlyDetail {
  month: string;
  monthlyCashCollected: number;
  cumulativeCashCollected: number;
  monthlyCashCollectedPercent: number | null;
  cumulativeCashCollectedPercent: number | null;
}

export interface CashCollectedTransaction {
  monthStart: string;
  month: string;
  cashDate: string;
  cashSource: string;
  sourceId: string | null;
  documentNumber: string | null;
  reference: string | null;
  contactName: string | null;
  sourceCurrencyCode: string | null;
  sourceAmount: number;
  currencyRate: number | null;
  baseCurrencyAmount: number;
  sourceStatus: string | null;
}

export interface CashCollectedApiResponse {
  code: number;
  message: string;
  data: {
    kpi: CashCollectedKpiSummary;
    monthlyDetail: CashCollectedMonthlyDetail[];
    transactions?: CashCollectedTransaction[];
  };
}

const CASH_SOURCE_LABELS: Record<string, string> = {
  INVOICE_PAYMENT: 'Invoice Payment',
  RECEIVE_OVERPAYMENT: 'Receive Overpayment',
};

/**
 * Builds the Cash Collected / Ledger popup rows from the cash-collected API payload,
 * attaching each month's cash events so the popup can drill into a month.
 */
export function buildCashCollectedLedgerRows(
  data: CashCollectedApiResponse['data'] | null | undefined
): CashCollectedLedgerDrillDownRow[] {
  const transactionsByMonth = new Map<string, CashCollectedTransactionRow[]>();
  for (const txn of data?.transactions ?? []) {
    const rows = transactionsByMonth.get(txn.month) ?? [];
    rows.push({
      date: txn.cashDate,
      source: CASH_SOURCE_LABELS[txn.cashSource] ?? txn.cashSource,
      documentNumber: txn.documentNumber,
      client: txn.contactName?.trim() || 'Unknown Client',
      reference: txn.reference,
      amount: Number(txn.baseCurrencyAmount ?? 0),
    });
    transactionsByMonth.set(txn.month, rows);
  }

  return (data?.monthlyDetail ?? []).map(row => {
    const transactions = (transactionsByMonth.get(row.month) ?? [])
      .sort((a, b) => a.date.localeCompare(b.date) || b.amount - a.amount);
    return {
      monthLabel: row.month,
      monthlyCashCollected: Number(row.monthlyCashCollected ?? 0),
      cumulativeCashCollected: Number(row.cumulativeCashCollected ?? 0),
      monthlyCashCollectedPercent: row.monthlyCashCollectedPercent == null
        ? null
        : Number(row.monthlyCashCollectedPercent),
      cumulativeCashCollectedPercent: row.cumulativeCashCollectedPercent == null
        ? null
        : Number(row.cumulativeCashCollectedPercent),
      drillable: transactions.length > 0,
      transactions,
    };
  });
}

export interface AgedDebtOver90DaysDrillDownRow {
  monthLabel: string;
  monthlyRevenue: number;
  cumulativeRevenue: number;
  outstandingInvoiceCount: number;
  totalOutstandingAtMonthEnd: number;
  agedInvoiceCountOver90Days: number;
  agedDebtOver90Days: number;
  agedDebtOver90Percent: number | null;
}

export interface AgedDebtBucketSummaryViewRow {
  monthLabel: string;
  agedDebt90Plus: number;
  aged90Percent: number | null;
  cashCollection: number;
  turnover: number;
  salesLedgerTotal: number;
  countBackDSO: number | null;
}

export interface AgedDebtBucketDetailViewRow {
  monthLabel: string;
  agedBucket: string;
  clientName: string;
  invoiceNumber: string;
  invoiceDate: string | null;
  dueDate: string | null;
  invoiceTotal: number;
  outstandingAtMonthEnd: number;
  daysOverdue: number;
}

export interface TurnoverDrillDownSummary {
  rows: TurnoverDrillDownSummaryRow[];
  totalSalesPermLabel: string;
  totalSalesPerm: number | null;
  totalSalesTempLabel: string;
  totalSalesTemp: number | null;
  totalTurnoverLabel: string;
  totalTurnover: number | null;
}

export interface TurnoverDrillDownSummaryRow {
  displayOrder: number;
  key: string;
  name: string;
  value: number;
}

export interface TurnoverDrillDownDetailRow {
  salesType: string;
  summaryKey: string;
  summaryName: string;
  date: string | null;
  source: string | null;
  description: string | null;
  reference: string | null;
  amount: number;
  amountPaid: number;
  accountCode: string | null;
  accountName: string | null;
  accountType: string | null;
}

export interface CostOfSalesDrillDownDetailRow {
  date: string | null;
  description: string | null;
  reference: string | null;
  amount: number;
  amountPaid: number;
  accountCode: string | null;
  accountName: string | null;
  accountType: string | null;
  accountClass: string | null;
}

export interface AdministrativeCostDrillDownSummaryRow {
  administrativeCostName: string;
  accountCode: string | null;
  totalAmount: number;
}

export interface AdministrativeCostDrillDownDetailRow {
  administrativeCostName: string;
  date: string | null;
  source: string | null;
  description: string | null;
  reference: string | null;
  amount: number;
  amountPaid: number;
  accountCode: string | null;
}

export interface FinancialOverviewDailyDrillDetailRow {
  kpiKey: string;
  kpiName: string | null;
  monthLabel: string;
  date: string | null;
  kpiValue: number | null;
  kpiPercent: number | null;
}

export interface ProgressionFinancialMetric {
  resultSetOrder: number;
  resultSetName: string;
  displayOrder: number;
  key: string;
  name: string;
  value: number;
}

interface FinancialDashboardMarginComparisonRow {
  monthStart: string;
  month: string;
  monthLabel: string;
  revenue: number;
  grossProfitMarginPercent: number | null;
  ebitdaMarginPercent: number | null;
  netProfitMarginPercent: number | null;
}

interface FinancialDashboardOperatingCostRow {
  monthStart: string;
  month: string;
  monthLabel: string;
  revenue: number;
  operatingCost: number;
  operatingCostPercent: number | null;
}

interface FinancialDashboardClientGrossProfitRow {
  clientName: string;
  revenue: number;
  grossProfit: number;
}

export interface RevenueConcentrationRow {
  rank: number;
  contactId: string;
  clientName: string;
  invoiceCount: number;
  revenue: number;
  revenueConcentrationPercent: number;
  grossProfit: number;
  grossProfitMarginPercent: number | null;
  totalCompanyRevenue: number;
  top10Revenue: number;
  top10RevenueConcentrationPercent: number;
  otherRevenue: number;
  otherRevenuePercent: number;
  otherGrossProfit?: number | null;
  otherGrossProfitMarginPercent?: number | null;
}

interface FinancialDashboardClientChaseRow {
  rowNumber: number;
  contactId: string;
  clientName: string;
  outstandingInvoiceCount: number;
  totalOutstanding: number;
  bucket0To30: number;
  bucket31To60: number;
  bucket61To90: number;
  bucket90Plus: number;
  billedLedgerAmount: number;
  dso: number | null;
  totalClients: number;
}

interface FinancialDashboardClientChaseDrillDownRow {
  contactId: string;
  clientName: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  invoiceStatus: string;
  invoiceDetails: string;
  itemType?: string;
  invoiceTotal: number;
  amountPaid: number;
  creditNotesApplied: number;
  overpaymentsApplied: number;
  prepaymentsApplied: number;
  outstandingAmount: number;
  daysOverdue: number;
  invoiceAgeDays: number;
}

interface FinancialDashboardAgedDebtRow {
  displayOrder: number;
  bucket: string;
  invoiceCount: number;
  amount: number;
}

interface FinancialDashboardPlacementRevenueRow extends Record<string, unknown> {
  monthStart?: string;
  month?: string;
  monthLabel?: string;
  revenueAccounts?: Record<string, number | null>;
  totalRevenue?: number;
}

interface FinancialDashboardGpmRow extends Record<string, unknown> {
  monthStart?: string;
  month?: string;
  monthLabel?: string;
  revenueAccountGpmPercentages?: Record<string, number | null>;
  totalGpm?: number | null;
}

interface FinancialDashboardOverviewRow {
  month: string;
  monthLabel: string;
  revenue: number;
  netFeeIncome: number;
  netFeeIncomePercent: number | null;
  grossProfitMarginPercent: number | null;
  operatingCost: number;
  ebitda: number;
  ebitdaMarginPercent: number | null;
  netProfit: number;
  netProfitMarginPercent: number | null;
}

interface FinancialDashboardDrillDetailRow {
  kpiKey: string;
  kpiName: string;
  monthNumber: number;
  month: string;
  monthLabel: string;
  monthStart: string;
  earliestDate: string | null;
  kpiValue: number | null;
  kpiPercent: number | null;
  revenue: number;
  nfi: number;
  operatingCost: number;
  ebitda: number;
  netProfit: number;
}

export interface RevenueByClientItem { contactId: string | null; clientName: string; revenue: number; invoiceCount: number; }
export interface MonthlyRevenueItem  { month: string; monthLabel: string; revenue: number; invoiceCount: number; }
export interface RevenuePeriodItem   { period: string; periodLabel: string; revenue: number; invoiceCount: number; }
export interface InvoiceStatusItem   { status: string; count: number; amount: number; }
export interface CashFlowTrendItem   { period: string; billedRevenue: number; cashCollected: number; }
export interface AgedDebtBucketItem  { bucket: string; amount: number; invoiceCount?: number; }
export interface DsoTrendItem        { month: string; monthLabel: string; dso: number | null; targetDays?: number; }
export interface CreditControlClientRow {
  rowNumber?: number;
  contactId?: string | null;
  clientName: string;
  outstandingInvoiceCount?: number;
  totalOutstanding: number;
  bucket0To30: number;
  bucket31To60: number;
  bucket61To90: number;
  bucket90Plus: number;
  billedLedgerAmount?: number;
  dso: number | null;
  totalClients?: number;
}

export interface ClientChaseListDrillDownDetailRow {
  contactId: string;
  clientName: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  invoiceStatus: string;
  invoiceDetails: string;
  itemType: string;
  invoiceTotal: number;
  amountPaid: number;
  creditNotesApplied: number;
  overpaymentsApplied: number;
  prepaymentsApplied: number;
  outstandingAmount: number;
  unallocatedCashAmount?: number;
  daysOverdue: number;
  invoiceAgeDays: number;
}

export interface FinancialOverviewPoint {
  period: string;
  periodLabel: string;
  totalIncome: number;
  totalExpenses: number;
  investments: number | null;
  savings: number | null;
  nfi?: number | null;
  grossProfit?: number | null;
  grossProfitMargin?: number | null;
  ebitda?: number | null;
  ebitdaMargin?: number | null;
  netProfit?: number | null;
  netProfitMargin?: number | null;
  operatingCostPercent?: number | null;
  monthNumber?: number;
  earliestDate?: string | null;
  kpiKey?: string;
  kpiName?: string;
  kpiValue?: number | null;
  kpiPercent?: number | null;
}

export interface InvoiceTableRow {
  contactId?:     string | null;
  invoiceNumber: string | null;
  clientName:    string | null;
  status:        string;
  invoiceDate:   string | null;
  dueDate:       string | null;
  total:         number;
  amountDue:     number;
  currencyCode:  string | null;
  type?:         string;
  subTotal?:     number;
  taxAmount?:    number;
  amountPaid?:   number;
  amountCredited?: number;
  reference?:    string | null;
  payments?:     { paymentDate: string | null; amount: number; reference: string | null }[];
  creditNotes?:  { date: string | null; appliedAmount: number; creditNoteNumber: string | null }[];
}

export interface ContactTableRow {
  contactName: string;
  email: string | null;
  type: string;
  status: string | null;
}

export interface PaymentTableRow {
  paymentDate: string;
  reference: string | null;
  amount: number;
  currencyCode: string | null;
}

export interface ChartData {
  revenueByClient:  RevenueByClientItem[];
  monthlyRevenue:   MonthlyRevenueItem[];
  revenueByPeriod:  RevenuePeriodItem[];
  invoicesByStatus: InvoiceStatusItem[];
  recentInvoices:   InvoiceTableRow[];
  recentContacts:   ContactTableRow[];
  recentPayments:   PaymentTableRow[];
  cashFlowTrend:     CashFlowTrendItem[];
  financialOverview: FinancialOverviewPoint[];
  agedDebtBuckets:   AgedDebtBucketItem[];
  creditControlClients: CreditControlClientRow[];
}

export interface EvertimeGpRow {
  name: string;
  revenue: number;
  grossProfit: number;
  currencyCode: string | null;
}

export interface EvertimePlacementRevenueRow {
  month: string;
  monthLabel: string;
  category: string;
  revenue: number;
  currencyCode: string | null;
}

export interface EvertimeGpMarginTrendPoint {
  month: string;
  monthLabel: string;
  category: string;
  gpmPct: number | null;
}

export interface EvertimeContractorTrendPoint {
  period: string;
  periodLabel: string;
  consultantName: string;
  contractorsWorked: number;
}

export interface EvertimeConsultantLeaderboardRow {
  consultant: string;
  revenue: number;
  grossProfit: number;
  timesheets: number;
  avgMargin: number | null;
  commissionPaid: number;
  currencyCode: string | null;
}

export interface EvertimeConsultantMonthlyPerformanceRow {
  monthLabel: string;
  consultant: string;
  invoiceCount: number;
  revenue: number;
  purchaseCost: number;
  grossProfit: number;
  timesheets: number;
  avgMargin: number | null;
  currencyCode: string | null;
}

export type EvertimeConsultantInvoiceTimesheetDetailRow = Record<string, unknown> & {
  consultantId: string;
  consultantName: string;
  monthLabel: string;
};

export interface EvertimeContractorWorkedDetailRow {
  monthLabel: string;
  candidateName: string;
  firstWorkDate: string;
  lastWorkDate: string;
  daysWorked: number;
}

export interface EvertimeClientPerformanceRow {
  rowNumber?: number;
  contactId?: string | null;
  client: string;
  contactStatus?: string | null;
  revenue: number;
  revenueInvoiceCount?: number;
  grossProfit: number;
  gpPct: number | null;
  permanentRevenue?: number;
  temporaryRevenue?: number;
  placements: number;
  contractors: number;
  outstandingBalance: number;
  outstandingInvoiceCount?: number;
  revenueConcentrationPercent?: number | null;
  totalClients?: number;
  currencyCode: string | null;
}

export interface EvertimeClientRevenueTrendPoint {
  month: string;
  monthLabel: string;
  client: string;
  revenue: number;
  currencyCode: string | null;
}

export interface EvertimeRevenuePerConsultantRow {
  name: string;
  revenue: number;
  currencyCode: string | null;
}

export interface EvertimeRevenuePerConsultantSummary {
  totalRevenue: number;
  recruiterCount: number;
  revenuePerRecruiter: number;
  currencyCode: string | null;
  breakdown: EvertimeRevenuePerConsultantRow[];
}

export interface EvertimeSummary {
  hasData: boolean;
  contractorsWorked: number;
  averageContractorMarginPct: number | null;
  clientsBilled: number;
  topClientsByGrossProfit: EvertimeGpRow[];
  topConsultantsByGrossProfit: EvertimeGpRow[];
  placementRevenueByMonth: EvertimePlacementRevenueRow[];
  gpMarginTrendByMonth: EvertimeGpMarginTrendPoint[];
  contractorsWorkedTrend: EvertimeContractorTrendPoint[];
  contractorsWorkedDetails: EvertimeContractorWorkedDetailRow[];
  contractorsWorkedMonthlyDetails: EvertimeContractorWorkedDetailRow[];
  consultantLeaderboard: EvertimeConsultantLeaderboardRow[];
  consultantMonthlyPerformance: EvertimeConsultantMonthlyPerformanceRow[];
  consultantInvoiceTimesheetDetails: EvertimeConsultantInvoiceTimesheetDetailRow[];
  clientPerformance: EvertimeClientPerformanceRow[];
  clientRevenueTrend: EvertimeClientRevenueTrendPoint[];
  revenuePerConsultant: EvertimeRevenuePerConsultantSummary | null;
}

interface EvertimeDashboardApiResponse {
  code: number;
  message: string;
  data: EvertimeDashboardData;
}

interface EvertimeDashboardData {
  scope: {
    compId: number;
    fromDate: string;
    toDate: string;
    invoiceStatusId: number;
  };
  numberOfClientsBilled: number;
  contractorsWorked: number;
  averageContractorMarginPercent: number | null;
  revenuePerRecruiter: number | null;
  top10ConsultantsByGrossProfit: EvertimeDashboardConsultantProfit[];
  consultantLeaderboard: EvertimeDashboardLeaderboardRow[];
  contractorsWorkedMonthly: EvertimeDashboardContractorMonth[];
  consultantMonthlyPerformance?: unknown;
  consultantPerformanceMonthly?: unknown;
  consultantLeaderboardMonthly?: unknown;
  consultantMonthlyBreakdown?: unknown;
  consultantMonthlyDrilldown?: unknown;
  consultantInvoiceTimesheetDetails?: unknown;
  contractorsWorkedDetails?: unknown;
  contractorsWorkedDetail?: unknown;
  contractorWorkedDetails?: unknown;
  uniqueContractorsWorked?: unknown;
  contractorsWorkedMonthlySummary?: unknown;
}

interface EvertimeDashboardConsultantProfit {
  consultantId: string;
  consultantName: string;
  salesRevenue: number;
  grossProfit: number;
  grossMarginPercent: number | null;
}

interface EvertimeDashboardLeaderboardRow {
  rank: number;
  consultantId: string;
  consultantName: string;
  revenue: number;
  grossProfit: number;
  timesheets: number;
  avgMarginPercent: number | null;
}

interface EvertimeDashboardContractorMonth {
  monthLabel: string;
  contractorsWorked: number;
}

export interface SyncStatusEvent {
  eventType: string;
  message: string;
  occurredAt: string; // ISO timestamp
}

export interface SyncStatusResponse {
  events: SyncStatusEvent[];
}

@Injectable({ providedIn: 'root' })
export class DashboardService {
  private readonly apiBase = environment.apiBaseUrl;

  constructor(private http: HttpClient) {}

  getKpis(
    startDate?: string | null,
    endDate?: string | null,
    _status?: string[],
    _contactId?: string | null,
    _invoiceType?: string
  ): Observable<DashboardKpis> {
    const params = new HttpParams()
      .set('StartDate', startDate ?? DEFAULT_DASHBOARD_FROM_DATE)
      .set('EndDate', endDate ?? DEFAULT_DASHBOARD_TO_DATE);

    return this.http
      .get<FinancialDashboardKpiResponse | { data: FinancialDashboardKpiResponse }>(
        `${this.apiBase}/dashboard/financial-dashboard`,
        { params }
      )
      .pipe(map(response => this.normalizeFinancialDashboardKpis(response)));
  }

  getCashCollected(
    startDate?: string | null,
    endDate?: string | null
  ): Observable<CashCollectedApiResponse> {
    const params = new HttpParams()
      .set('startDate', startDate ?? DEFAULT_DASHBOARD_FROM_DATE)
      .set('endDate', endDate ?? DEFAULT_DASHBOARD_TO_DATE);

    return this.http.get<CashCollectedApiResponse>(
      `${this.apiBase}/dashboard/cash-collected`,
      { params }
    );
  }

  private normalizePlacementRevenueRows(
    rows: FinancialDashboardPlacementRevenueRow[]
  ): EvertimePlacementRevenueRow[] {
    const metadataKeys = new Set([
      'resultsetorder',
      'resultsetname',
      'monthstart',
      'month',
      'monthlabel',
      'revenueaccounts',
      'totalrevenue',
      'totalplacementrevenue',
    ]);

    return rows.flatMap(row => {
      const month = String(row['month'] ?? row['Month'] ?? '');
      const monthLabel = String(row['monthLabel'] ?? row['MonthLabel'] ?? month);

      const revenueAccounts = row['revenueAccounts'] ?? row['RevenueAccounts'];
      if (revenueAccounts && typeof revenueAccounts === 'object' && !Array.isArray(revenueAccounts)) {
        return Object.entries(revenueAccounts).flatMap(([accountName, rawValue]) => {
          if (rawValue == null) return [];

          const revenue = Number(rawValue);
          if (!Number.isFinite(revenue)) return [];
          const displayName = accountName.replace(/\s*\([^()]+\)\s*$/, '').trim() || accountName;

          return [{
            month,
            monthLabel,
            category: displayName,
            revenue,
            currencyCode: 'GBP',
          } satisfies EvertimePlacementRevenueRow];
        });
      }

      return Object.entries(row).flatMap(([key, rawValue]) => {
        const normalizedKey = key.replace(/[\s_-]/g, '').toLowerCase();
        if (metadataKeys.has(normalizedKey) || rawValue == null) return [];

        const revenue = Number(rawValue);
        if (!Number.isFinite(revenue)) return [];

        return [{
          month,
          monthLabel,
          category: key,
          revenue,
          currencyCode: 'GBP',
        } satisfies EvertimePlacementRevenueRow];
      });
    });
  }

  private normalizeGpmRows(rows: FinancialDashboardGpmRow[]): EvertimeGpMarginTrendPoint[] {
    return rows.flatMap(row => {
      const month = String(row['month'] ?? row['Month'] ?? '');
      const monthLabel = String(row['monthLabel'] ?? row['MonthLabel'] ?? month);
      const percentages = row['revenueAccountGpmPercentages'] ?? row['RevenueAccountGpmPercentages'];
      if (!percentages || typeof percentages !== 'object' || Array.isArray(percentages)) return [];

      return Object.entries(percentages).map(([accountName, rawValue]) => ({
        month,
        monthLabel,
        category: accountName
          .replace(/\s+GPM\s*%\s*$/i, '')
          .replace(/\s*\([^()]+\)\s*$/, '')
          .trim() || accountName,
        gpmPct: rawValue == null ? null : this.readNullableNumber(rawValue),
      }));
    });
  }

  private normalizeFinancialDashboardKpis(
    response: FinancialDashboardKpiResponse | { data: FinancialDashboardKpiResponse }
  ): DashboardKpis {
    const payload = 'data' in response ? response.data : response;
    const startDate = payload.scope?.startDate;
    const endDate = payload.scope?.endDate;
    const placementRevenueRows = this.findArrayProperty<FinancialDashboardPlacementRevenueRow>(
      response,
      'placementRevenuePerMonth'
    );
    const gpmRows = this.findArrayProperty<FinancialDashboardGpmRow>(
      response,
      'gpmPermVsTempByMonth'
    );
    const agedDebtRows = this.findArrayProperty<FinancialDashboardAgedDebtRow>(
      response,
      'agedDebtBuckets'
    );
    const clientChaseRows = this.findArrayProperty<FinancialDashboardClientChaseRow>(
      response,
      'clientChaseList'
    );
    const clientChaseDrillDownRows =
      this.findArrayProperty<FinancialDashboardClientChaseDrillDownRow>(
        response,
        'clientChaseListDrillDownDetail'
      );
    const revenueConcentrationRows = this.findArrayProperty<RevenueConcentrationRow>(
      response,
      'revenueConcentration'
    );
    const topClientGrossProfitRows = this.findArrayProperty<FinancialDashboardClientGrossProfitRow>(
      response,
      'top10ClientsByGrossProfit'
    );
    const clientPerformanceRows = this.findArrayProperty<Record<string, unknown>>(
      response,
      'clientPerformance'
    );
    const progressionRows = this.findArrayProperty<ProgressionFinancialMetric>(
      response,
      'progressionOfFinancialMetrics'
    );
    const cashCollectedLedgerDrillDownRows =
      this.findArrayProperty<CashCollectedLedgerDrillDownRow>(
        response,
        'cashCollectedLedgerDrillDown'
      );
    const agedDebtOver90DaysDrillDownRows =
      this.findArrayProperty<AgedDebtOver90DaysDrillDownRow>(
        response,
        'agedDebtOver90DaysDrillDown'
      );
    const namedAgedDebtBucketSummaryViewRows =
      this.findArrayProperty<AgedDebtBucketSummaryViewRow>(
        response,
        'agedDebtBucketSummaryView'
      );
    const agedDebtBucketSummaryViewRows = namedAgedDebtBucketSummaryViewRows.length > 0
      ? namedAgedDebtBucketSummaryViewRows
      : this.findArrayByRequiredProperties<AgedDebtBucketSummaryViewRow>(response, [
          'monthLabel',
          'agedDebt90Plus',
          'aged90Percent',
          'cashCollection',
          'turnover',
          'salesLedgerTotal',
          'countBackDSO',
        ]);
    const namedAgedDebtBucketDetailViewRows =
      this.findArrayProperty<AgedDebtBucketDetailViewRow>(response, 'agedDebtBucketDetailView');
    const agedDebtBucketDetailViewRows = namedAgedDebtBucketDetailViewRows.length > 0
      ? namedAgedDebtBucketDetailViewRows
      : this.findArrayByRequiredProperties<AgedDebtBucketDetailViewRow>(response, [
          'monthLabel',
          'agedBucket',
          'clientName',
          'invoiceNumber',
          'invoiceDate',
          'dueDate',
          'invoiceTotal',
          'outstandingAtMonthEnd',
          'daysOverdue',
        ]);
    const turnoverDrillDownSummaryRows =
      this.findArrayProperty<Record<string, unknown>>(response, 'turnoverDrillDownSummary');
    const turnoverDrillDownSummaryObject = turnoverDrillDownSummaryRows[0] ??
      this.findObjectProperty<Record<string, unknown>>(response, 'turnoverDrillDownSummary');
    const turnoverSummaryMetric = (propertyName: string): { label: string; value: number | null } => {
      const expectedName = this.normalizeFinancialKpiKey(propertyName);
      const matchingRow = turnoverDrillDownSummaryRows.find(row => {
        const rowName = this.readCaseInsensitiveProperty(row, 'name');
        const rowKey = this.readCaseInsensitiveProperty(row, 'key');
        return [rowName, rowKey]
          .filter((value): value is string => typeof value === 'string')
          .some(value => expectedName === this.normalizeFinancialKpiKey(value));
      });
      const apiLabel = matchingRow
        ? this.readFirstCaseInsensitiveProperty(matchingRow, ['name', 'label', 'title'])
        : turnoverDrillDownSummaryObject
          ? this.readFirstCaseInsensitiveProperty(turnoverDrillDownSummaryObject, [
              `${propertyName}Label`,
              `${propertyName}Name`,
            ])
          : null;
      const label = typeof apiLabel === 'string' && apiLabel.trim()
        ? apiLabel.trim()
        : propertyName.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, value => value.toUpperCase());
      const directValue = turnoverDrillDownSummaryObject
        ? this.readCaseInsensitiveProperty(turnoverDrillDownSummaryObject, propertyName)
        : null;
      if (directValue !== null && directValue !== undefined) {
        const numericValue = Number(directValue);
        return { label, value: Number.isFinite(numericValue) ? numericValue : null };
      }
      const rowValue = matchingRow
        ? this.readCaseInsensitiveProperty(matchingRow, 'value')
        : null;
      if (rowValue === null || rowValue === undefined) return { label, value: null };
      const numericValue = Number(rowValue);
      return { label, value: Number.isFinite(numericValue) ? numericValue : null };
    };
    const totalSalesPerm = turnoverSummaryMetric('totalSalesPerm');
    const totalSalesTemp = turnoverSummaryMetric('totalSalesTemp');
    const totalTurnover = turnoverSummaryMetric('totalTurnover');
    const dynamicTurnoverSummaryRows = turnoverDrillDownSummaryRows
      .map(row => ({
        displayOrder: Number(this.readCaseInsensitiveProperty(row, 'displayOrder')),
        key: String(this.readCaseInsensitiveProperty(row, 'key') ?? ''),
        name: String(
          this.readFirstCaseInsensitiveProperty(row, ['name', 'label', 'title']) ?? ''
        ),
        value: Number(this.readCaseInsensitiveProperty(row, 'value')),
      }))
      .filter(row => row.name.trim() && Number.isFinite(row.value) && row.value !== 0)
      .sort((left, right) => left.displayOrder - right.displayOrder);
    const namedTurnoverDrillDownDetailRows =
      this.findArrayProperty<Record<string, unknown>>(response, 'turnoverDrillDownDetail');
    const turnoverDrillDownDetailRows = namedTurnoverDrillDownDetailRows.length > 0
      ? namedTurnoverDrillDownDetailRows
      : this.findArrayByRequiredProperties<Record<string, unknown>>(response, [
          'date',
          'source',
          'description',
          'reference',
          'amount',
          'amountPaid',
          'accountCode',
        ]);
    const namedCostOfSalesDrillDownDetailRows =
      this.findArrayProperty<Record<string, unknown>>(response, 'costOfSalesDrillDownDetail');
    const costOfSalesDrillDownDetailRows = namedCostOfSalesDrillDownDetailRows.length > 0
      ? namedCostOfSalesDrillDownDetailRows
      : this.findArrayByRequiredProperties<Record<string, unknown>>(response, [
          'date',
          'description',
          'reference',
          'amount',
          'amountPaid',
          'accountCode',
          'accountName',
          'accountType',
          'accountClass',
        ]);
    const namedAdministrativeCostDrillDownSummaryRows =
      this.findArrayProperty<AdministrativeCostDrillDownSummaryRow>(
        response,
        'administrativeCostDrillDownSummary'
      );
    const administrativeCostDrillDownSummaryRows =
      namedAdministrativeCostDrillDownSummaryRows.length > 0
        ? namedAdministrativeCostDrillDownSummaryRows
        : this.findArrayByRequiredProperties<AdministrativeCostDrillDownSummaryRow>(response, [
            'administrativeCostName',
            'accountCode',
            'totalAmount',
          ]);
    const namedAdministrativeCostDrillDownDetailRows =
      this.findArrayProperty<Record<string, unknown>>(
        response,
        'administrativeCostDrillDownDetail'
      );
    const administrativeCostDrillDownDetailRows =
      namedAdministrativeCostDrillDownDetailRows.length > 0
        ? namedAdministrativeCostDrillDownDetailRows
        : this.findArrayByRequiredProperties<Record<string, unknown>>(response, [
            'administrativeCostName',
            'date',
            'source',
            'description',
            'reference',
            'amount',
            'amountPaid',
            'accountCode',
          ]);
    const namedFinancialOverviewDailyDrillDetailRows =
      this.findArrayProperty<Record<string, unknown>>(
        response,
        'financialOverviewDailyDrillDetail'
      );
    const financialOverviewDailyDrillDetailRows =
      namedFinancialOverviewDailyDrillDetailRows.length > 0
        ? namedFinancialOverviewDailyDrillDetailRows
        : this.findArrayByRequiredProperties<Record<string, unknown>>(response, [
            'kpiKey',
            'monthLabel',
            'date',
            'kpiValue',
            'kpiPercent',
          ]);

    return {
      periodLabel: startDate && endDate ? `${startDate} to ${endDate}` : null,
      progressionOfFinancialMetrics: progressionRows
        .map(row => ({
          resultSetOrder: Number(row.resultSetOrder),
          resultSetName: row.resultSetName,
          displayOrder: Number(row.displayOrder),
          key: row.key,
          name: row.name,
          value: Number(row.value),
        }))
        .filter(row => Number.isFinite(row.value) && row.value !== 0)
        .sort((left, right) => left.displayOrder - right.displayOrder),
      cashCollectedLedgerDrillDown: cashCollectedLedgerDrillDownRows.map(row => ({
        monthLabel: row.monthLabel,
        monthlyCashCollected: Number(row.monthlyCashCollected ?? 0),
        cumulativeCashCollected: Number(row.cumulativeCashCollected ?? 0),
        monthlyCashCollectedPercent: row.monthlyCashCollectedPercent == null
          ? null
          : Number(row.monthlyCashCollectedPercent),
        cumulativeCashCollectedPercent: row.cumulativeCashCollectedPercent == null
          ? null
          : Number(row.cumulativeCashCollectedPercent),
      })),
      agedDebtOver90DaysDrillDown: agedDebtOver90DaysDrillDownRows.map(row => ({
        monthLabel: row.monthLabel,
        monthlyRevenue: Number(row.monthlyRevenue ?? 0),
        cumulativeRevenue: Number(row.cumulativeRevenue ?? 0),
        outstandingInvoiceCount: Number(row.outstandingInvoiceCount ?? 0),
        totalOutstandingAtMonthEnd: Number(row.totalOutstandingAtMonthEnd ?? 0),
        agedInvoiceCountOver90Days: Number(row.agedInvoiceCountOver90Days ?? 0),
        agedDebtOver90Days: Number(row.agedDebtOver90Days ?? 0),
        agedDebtOver90Percent: row.agedDebtOver90Percent == null
          ? null
          : Number(row.agedDebtOver90Percent),
      })),
      agedDebtBucketSummaryView: agedDebtBucketSummaryViewRows.map(row => ({
        monthLabel: row.monthLabel,
        agedDebt90Plus: Number(row.agedDebt90Plus ?? 0),
        aged90Percent: row.aged90Percent == null ? null : Number(row.aged90Percent),
        cashCollection: Number(row.cashCollection ?? 0),
        turnover: Number(row.turnover ?? 0),
        salesLedgerTotal: Number(row.salesLedgerTotal ?? 0),
        countBackDSO: row.countBackDSO == null ? null : Number(row.countBackDSO),
      })),
      agedDebtBucketDetailView: agedDebtBucketDetailViewRows.map(row => ({
        monthLabel: row.monthLabel,
        agedBucket: row.agedBucket,
        clientName: row.clientName,
        invoiceNumber: row.invoiceNumber,
        invoiceDate: row.invoiceDate,
        dueDate: row.dueDate,
        invoiceTotal: Number(row.invoiceTotal ?? 0),
        outstandingAtMonthEnd: Number(row.outstandingAtMonthEnd ?? 0),
        daysOverdue: Number(row.daysOverdue ?? 0),
      })),
      turnoverDrillDownSummary: turnoverDrillDownSummaryObject
        ? {
            rows: dynamicTurnoverSummaryRows,
            totalSalesPermLabel: totalSalesPerm.label,
            totalSalesPerm: totalSalesPerm.value,
            totalSalesTempLabel: totalSalesTemp.label,
            totalSalesTemp: totalSalesTemp.value,
            totalTurnoverLabel: totalTurnover.label,
            totalTurnover: totalTurnover.value,
          }
        : undefined,
      turnoverDrillDownDetail: turnoverDrillDownDetailRows.map(row => ({
        salesType: String(
          this.readFirstCaseInsensitiveProperty(row, ['salesType', 'saleType', 'category']) ?? ''
        ),
        summaryKey: String(
          this.readFirstCaseInsensitiveProperty(row, [
            'summaryKey', 'revenueKey', 'kpiKey', 'key', 'accountCode'
          ]) ?? ''
        ),
        summaryName: String(
          this.readFirstCaseInsensitiveProperty(row, [
            'summaryName', 'revenueName', 'name', 'category', 'accountName'
          ]) ?? ''
        ),
        date: this.readNullableString(
          this.readFirstCaseInsensitiveProperty(row, ['date', 'transactionDate'])
        ),
        source: this.readNullableString(this.readFirstCaseInsensitiveProperty(row, ['source'])),
        description: this.readNullableString(
          this.readFirstCaseInsensitiveProperty(row, ['description'])
        ),
        reference: this.readNullableString(
          this.readFirstCaseInsensitiveProperty(row, ['reference'])
        ),
        amount: Number(this.readFirstCaseInsensitiveProperty(row, ['amount']) ?? 0),
        amountPaid: Number(this.readFirstCaseInsensitiveProperty(row, ['amountPaid']) ?? 0),
        accountCode: this.readNullableString(
          this.readFirstCaseInsensitiveProperty(row, ['accountCode'])
        ),
        accountName: this.readNullableString(
          this.readFirstCaseInsensitiveProperty(row, ['accountName', 'accName'])
        ),
        accountType: this.readNullableString(
          this.readFirstCaseInsensitiveProperty(row, ['accountType', 'accType'])
        ),
      })),
      costOfSalesDrillDownDetail: costOfSalesDrillDownDetailRows.map(row => ({
        date: this.readNullableString(
          this.readFirstCaseInsensitiveProperty(row, ['date', 'transactionDate'])
        ),
        description: this.readNullableString(
          this.readFirstCaseInsensitiveProperty(row, ['description'])
        ),
        reference: this.readNullableString(
          this.readFirstCaseInsensitiveProperty(row, ['reference'])
        ),
        amount: Number(this.readFirstCaseInsensitiveProperty(row, ['amount']) ?? 0),
        amountPaid: Number(this.readFirstCaseInsensitiveProperty(row, ['amountPaid']) ?? 0),
        accountCode: this.readNullableString(
          this.readFirstCaseInsensitiveProperty(row, ['accountCode'])
        ),
        accountName: this.readNullableString(
          this.readFirstCaseInsensitiveProperty(row, ['accountName', 'accName'])
        ),
        accountType: this.readNullableString(
          this.readFirstCaseInsensitiveProperty(row, ['accountType', 'accType'])
        ),
        accountClass: this.readNullableString(
          this.readFirstCaseInsensitiveProperty(row, ['accountClass'])
        ),
      })),
      administrativeCostDrillDownSummary: administrativeCostDrillDownSummaryRows.map(row => ({
        administrativeCostName: row.administrativeCostName,
        accountCode: row.accountCode,
        totalAmount: Number(row.totalAmount ?? 0),
      })),
      administrativeCostDrillDownDetail: administrativeCostDrillDownDetailRows.map(row => ({
        administrativeCostName: String(
          this.readFirstCaseInsensitiveProperty(row, [
            'administrativeCostName',
            'adminCostName',
            'costName',
          ]) ?? ''
        ),
        date: this.readNullableString(
          this.readFirstCaseInsensitiveProperty(row, ['date', 'transactionDate'])
        ),
        source: this.readNullableString(this.readFirstCaseInsensitiveProperty(row, ['source'])),
        description: this.readNullableString(
          this.readFirstCaseInsensitiveProperty(row, ['description'])
        ),
        reference: this.readNullableString(
          this.readFirstCaseInsensitiveProperty(row, ['reference'])
        ),
        amount: Number(this.readFirstCaseInsensitiveProperty(row, ['amount']) ?? 0),
        amountPaid: Number(this.readFirstCaseInsensitiveProperty(row, ['amountPaid']) ?? 0),
        accountCode: this.readNullableString(
          this.readFirstCaseInsensitiveProperty(row, ['accountCode'])
        ),
      })),
      financialOverviewDailyDrillDetail: financialOverviewDailyDrillDetailRows.map(row => ({
        kpiKey: this.normalizeFinancialKpiKey(String(
          this.readFirstCaseInsensitiveProperty(row, ['kpiKey', 'key']) ?? ''
        )),
        kpiName: this.readNullableString(
          this.readFirstCaseInsensitiveProperty(row, ['kpiName', 'name'])
        ),
        monthLabel: String(
          this.readFirstCaseInsensitiveProperty(row, ['monthLabel']) ?? ''
        ),
        date: this.readNullableString(
          this.readFirstCaseInsensitiveProperty(row, ['date', 'transactionDate'])
        ),
        kpiValue: this.readNullableNumber(
          this.readFirstCaseInsensitiveProperty(row, ['kpiValue', 'value'])
        ),
        kpiPercent: this.readNullableNumber(
          this.readFirstCaseInsensitiveProperty(row, ['kpiPercent', 'percentage', 'percent'])
        ),
      })),
      kpis: (payload.kpis ?? []).map(card => {
        const normalizedKey = this.normalizeFinancialKpiKey(card.key);
        const normalizedName = this.normalizeFinancialKpiKey(card.name);
        const key = [normalizedKey, normalizedName].some(value =>
          value === 'number_of_client_billed' || value === 'number_of_clients_billed'
        )
          ? 'clients_billed'
          : normalizedKey;
        return {
          key,
          name: card.name,
          value: card.value == null ? null : Number(card.value),
          format: this.financialKpiFormat(key),
          note: null,
          sourceState: card.value == null ? 'unavailable' : 'current',
        };
      }),
      marginComparison: (payload.marginComparison ?? []).map(row => ({
        period: row.month,
        periodLabel: row.monthLabel,
        totalIncome: Number(row.revenue ?? 0),
        totalExpenses: 0,
        investments: null,
        savings: null,
        grossProfitMargin: row.grossProfitMarginPercent == null
          ? null
          : Number(row.grossProfitMarginPercent),
        ebitdaMargin: row.ebitdaMarginPercent == null
          ? null
          : Number(row.ebitdaMarginPercent),
        netProfitMargin: row.netProfitMarginPercent == null
          ? null
          : Number(row.netProfitMarginPercent),
      })),
      operatingCostPercentOfRevenue: (payload.operatingCostPercentOfRevenue ?? []).map(row => ({
        period: row.month,
        periodLabel: row.monthLabel,
        totalIncome: Number(row.revenue ?? 0),
        totalExpenses: Number(row.operatingCost ?? 0),
        investments: null,
        savings: null,
        operatingCostPercent: row.operatingCostPercent == null
          ? null
          : Number(row.operatingCostPercent),
      })),
      financialOverview: (payload.financialOverview ?? []).map(row => ({
        period: row.month,
        periodLabel: row.monthLabel,
        totalIncome: Number(row.revenue ?? 0),
        totalExpenses: Number(row.operatingCost ?? 0),
        investments: null,
        savings: null,
        nfi: Number(row.netFeeIncome ?? 0),
        grossProfit: Number(row.netFeeIncome ?? 0),
        grossProfitMargin: row.grossProfitMarginPercent,
        ebitda: Number(row.ebitda ?? 0),
        ebitdaMargin: row.ebitdaMarginPercent,
        netProfit: Number(row.netProfit ?? 0),
        netProfitMargin: row.netProfitMarginPercent,
      })),
      financialOverviewDrillDetail: (payload.financialOverviewDrillDetail ?? []).map(row => ({
        period: row.month,
        periodLabel: row.monthLabel,
        totalIncome: Number(row.revenue ?? 0),
        totalExpenses: Number(row.operatingCost ?? 0),
        investments: null,
        savings: null,
        nfi: Number(row.nfi ?? 0),
        ebitda: Number(row.ebitda ?? 0),
        netProfit: Number(row.netProfit ?? 0),
        monthNumber: row.monthNumber,
        earliestDate: row.earliestDate,
        kpiKey: this.normalizeFinancialKpiKey(row.kpiKey),
        kpiName: row.kpiName,
        kpiValue: row.kpiValue == null ? null : Number(row.kpiValue),
        kpiPercent: row.kpiPercent == null ? null : Number(row.kpiPercent),
      })),
      placementRevenuePerMonth: this.normalizePlacementRevenueRows(placementRevenueRows),
      gpmPermVsTempByMonth: this.normalizeGpmRows(gpmRows),
      agedDebtBuckets: [...agedDebtRows]
        .sort((a, b) => Number(a.displayOrder ?? 0) - Number(b.displayOrder ?? 0))
        .map(row => ({
          bucket: row.bucket,
          invoiceCount: Number(row.invoiceCount ?? 0),
          amount: Number(row.amount ?? 0),
        })),
      clientChaseList: [...clientChaseRows]
        .sort((a, b) => Number(a.rowNumber) - Number(b.rowNumber))
        .map(row => ({
          rowNumber: Number(row.rowNumber),
          contactId: row.contactId,
          clientName: row.clientName,
          outstandingInvoiceCount: Number(row.outstandingInvoiceCount),
          totalOutstanding: Number(row.totalOutstanding),
          bucket0To30: Number(row.bucket0To30),
          bucket31To60: Number(row.bucket31To60),
          bucket61To90: Number(row.bucket61To90),
          bucket90Plus: Number(row.bucket90Plus),
          billedLedgerAmount: Number(row.billedLedgerAmount),
          dso: row.dso == null ? null : Number(row.dso),
          totalClients: Number(row.totalClients),
        })),
      clientChaseListDrillDownDetail: clientChaseDrillDownRows.map(row => ({
        contactId: row.contactId,
        clientName: row.clientName,
        invoiceNumber: row.invoiceNumber,
        invoiceDate: row.invoiceDate,
        dueDate: row.dueDate,
        invoiceStatus: row.invoiceStatus,
        invoiceDetails: row.invoiceDetails,
        itemType: this.readClientChaseItemType(row),
        invoiceTotal: Number(row.invoiceTotal ?? 0),
        amountPaid: Number(row.amountPaid ?? 0),
        creditNotesApplied: Number(row.creditNotesApplied ?? 0),
        overpaymentsApplied: Number(row.overpaymentsApplied ?? 0),
        prepaymentsApplied: Number(row.prepaymentsApplied ?? 0),
        outstandingAmount: Number(row.outstandingAmount ?? 0),
        daysOverdue: Number(row.daysOverdue ?? 0),
        invoiceAgeDays: Number(row.invoiceAgeDays ?? 0),
      })),
      revenueConcentration: [...revenueConcentrationRows]
        .sort((a, b) => Number(a.rank) - Number(b.rank))
        .map(row => ({
          rank: Number(row.rank),
          contactId: row.contactId,
          clientName: row.clientName,
          invoiceCount: Number(row.invoiceCount),
          revenue: Number(row.revenue),
          revenueConcentrationPercent: Number(row.revenueConcentrationPercent),
          grossProfit: Number(row.grossProfit),
          grossProfitMarginPercent: row.grossProfitMarginPercent == null
            ? null
            : Number(row.grossProfitMarginPercent),
          totalCompanyRevenue: Number(row.totalCompanyRevenue),
          top10Revenue: Number(row.top10Revenue),
          top10RevenueConcentrationPercent: Number(row.top10RevenueConcentrationPercent),
          otherRevenue: Number(row.otherRevenue),
          otherRevenuePercent: Number(row.otherRevenuePercent),
          otherGrossProfit: row.otherGrossProfit == null ? null : Number(row.otherGrossProfit),
          otherGrossProfitMarginPercent: row.otherGrossProfitMarginPercent == null
            ? null
            : Number(row.otherGrossProfitMarginPercent),
        })),
      top10ClientsByGrossProfit: topClientGrossProfitRows.map(row => ({
        name: row.clientName,
        revenue: Number(row.revenue),
        grossProfit: Number(row.grossProfit),
        currencyCode: 'GBP',
      })),
      clientPerformance: clientPerformanceRows
        .map((row): EvertimeClientPerformanceRow | null => {
          const client = this.readNullableString(
            row['clientName'] ?? row['ClientName'] ?? row['client'] ?? row['Client']
          );
          if (!client) return null;

          return {
            rowNumber: Number(row['rowNumber'] ?? row['RowNumber'] ?? 0),
            contactId: this.readNullableString(row['contactId'] ?? row['ContactId']),
            client,
            contactStatus: this.readNullableString(row['contactStatus'] ?? row['ContactStatus']),
            revenue: Number(row['revenue'] ?? row['Revenue'] ?? 0),
            revenueInvoiceCount: Number(
              row['revenueInvoiceCount'] ?? row['RevenueInvoiceCount'] ?? 0
            ),
            grossProfit: Number(row['grossProfit'] ?? row['GrossProfit'] ?? 0),
            gpPct: this.readNullableNumber(
              row['grossProfitMarginPercent'] ?? row['GrossProfitMarginPercent'] ??
              row['gpPct'] ?? row['GpPct']
            ),
            permanentRevenue: Number(
              row['permanentRevenue'] ?? row['PermanentRevenue'] ?? 0
            ),
            temporaryRevenue: Number(
              row['temporaryRevenue'] ?? row['TemporaryRevenue'] ?? 0
            ),
            placements: Number(row['placements'] ?? row['Placements'] ?? 0),
            contractors: Number(
              row['contractors'] ?? row['Contractors'] ??
              row['contractorCount'] ?? row['ContractorCount'] ?? 0
            ),
            outstandingBalance: Number(
              row['outstandingBalance'] ?? row['OutstandingBalance'] ??
              row['totalOutstanding'] ?? row['TotalOutstanding'] ?? 0
            ),
            outstandingInvoiceCount: Number(
              row['outstandingInvoiceCount'] ?? row['OutstandingInvoiceCount'] ?? 0
            ),
            revenueConcentrationPercent: this.readNullableNumber(
              row['revenueConcentrationPercent'] ?? row['RevenueConcentrationPercent']
            ),
            totalClients: Number(row['totalClients'] ?? row['TotalClients'] ?? 0),
            currencyCode: this.readNullableString(
              row['currencyCode'] ?? row['CurrencyCode']
            ) ?? 'GBP',
          };
        })
        .filter((row): row is EvertimeClientPerformanceRow => row !== null),
    };
  }

  private findArrayProperty<T>(source: unknown, propertyName: string, depth = 0): T[] {
    if (!source || typeof source !== 'object' || depth > 3) return [];

    const record = source as Record<string, unknown>;
    const matchingKey = Object.keys(record).find(
      key => key.toLowerCase() === propertyName.toLowerCase()
    );
    if (matchingKey && Array.isArray(record[matchingKey])) {
      return record[matchingKey] as T[];
    }

    for (const value of Object.values(record)) {
      if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
      const nested = this.findArrayProperty<T>(value, propertyName, depth + 1);
      if (nested.length > 0) return nested;
    }

    return [];
  }

  private findArrayByRequiredProperties<T>(
    source: unknown,
    requiredProperties: string[],
    depth = 0
  ): T[] {
    if (!source || typeof source !== 'object' || depth > 4) return [];

    if (Array.isArray(source)) {
      const firstRow = source.find(item => item && typeof item === 'object');
      if (firstRow) {
        const rowKeys = new Set(
          Object.keys(firstRow as Record<string, unknown>).map(key => key.toLowerCase())
        );
        const containsRequiredProperties = requiredProperties.every(property =>
          rowKeys.has(property.toLowerCase())
        );
        if (containsRequiredProperties) return source as T[];
      }

      for (const item of source) {
        const nested = this.findArrayByRequiredProperties<T>(
          item,
          requiredProperties,
          depth + 1
        );
        if (nested.length > 0) return nested;
      }
      return [];
    }

    for (const value of Object.values(source as Record<string, unknown>)) {
      const nested = this.findArrayByRequiredProperties<T>(
        value,
        requiredProperties,
        depth + 1
      );
      if (nested.length > 0) return nested;
    }

    return [];
  }

  private findObjectProperty<T>(source: unknown, propertyName: string, depth = 0): T | null {
    if (!source || typeof source !== 'object' || depth > 4) return null;

    const record = source as Record<string, unknown>;
    const matchingKey = Object.keys(record).find(
      key => key.toLowerCase() === propertyName.toLowerCase()
    );
    const matchingValue = matchingKey ? record[matchingKey] : null;
    if (matchingValue && typeof matchingValue === 'object' && !Array.isArray(matchingValue)) {
      return matchingValue as T;
    }

    for (const value of Object.values(record)) {
      const nested = this.findObjectProperty<T>(value, propertyName, depth + 1);
      if (nested) return nested;
    }

    return null;
  }

  private readCaseInsensitiveProperty(
    record: Record<string, unknown>,
    propertyName: string
  ): unknown {
    const matchingKey = Object.keys(record).find(
      key => key.toLowerCase() === propertyName.toLowerCase()
    );
    return matchingKey ? record[matchingKey] : null;
  }

  private readFirstCaseInsensitiveProperty(
    record: Record<string, unknown>,
    propertyNames: string[]
  ): unknown {
    for (const propertyName of propertyNames) {
      const value = this.readCaseInsensitiveProperty(record, propertyName);
      if (value !== null && value !== undefined) return value;
    }
    return null;
  }

  /**
   * The Client Chase List DrillDown Detail result set names its item type column
   * inconsistently across report versions, so accept the known spellings rather
   * than binding to one and silently rendering a blank column.
   */
  private readClientChaseItemType(row: FinancialDashboardClientChaseDrillDownRow): string {
    const value = this.readFirstCaseInsensitiveProperty(row as unknown as Record<string, unknown>, [
      'itemType',
      'invoiceItemType',
      'lineItemType',
    ]);
    return value == null ? '' : String(value).trim();
  }

  private normalizeFinancialKpiKey(key: string): string {
    return key
      .trim()
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      .replace(/[\s-]+/g, '_')
      .toLowerCase();
  }

  private financialKpiFormat(key: string): KpiValue['format'] {
    if (key === 'clients_billed') return 'number';
    if (key === 'debtor_days') return 'days';
    if (key === 'nfi' || key === 'ebitda' || key === 'net_profit' || key === 'total_payout') return 'currency';
    return 'percent';
  }

  getChartData(
    drillLevel: DrillLevel = 'year',
    periodContext: string | null = null,
    startDate?: string | null,
    endDate?: string | null,
    status?: string[],
    contactId?: string | null,
    invoiceType?: string,
    cashFlowGranularity?: string,
    metric?: string
  ): Observable<ChartData> {
    let url = `${this.apiBase}/dashboard/chart-data?drillLevel=${drillLevel}`;
    if (periodContext) {
      url += `&periodContext=${encodeURIComponent(periodContext)}`;
    }
    if (startDate) {
      url += `&startDate=${encodeURIComponent(startDate)}`;
    }
    if (endDate) {
      url += `&endDate=${encodeURIComponent(endDate)}`;
    }
    if (status && status.length > 0) {
      url += `&status=${encodeURIComponent(status.join(','))}`;
    }
    if (contactId) {
      url += `&contactId=${encodeURIComponent(contactId)}`;
    }
    if (invoiceType && invoiceType.toLowerCase() !== 'all') {
      url += `&invoiceType=${encodeURIComponent(invoiceType)}`;
    }
    if (cashFlowGranularity) {
      url += `&cashFlowGranularity=${encodeURIComponent(cashFlowGranularity)}`;
    }
    if (metric) {
      url += `&metric=${encodeURIComponent(metric)}`;
    }
    return this.http.get<Record<string, unknown>>(url).pipe(
      map(raw => this.normalizeChartData(raw)),
    );
  }
  
  getEvertimeSummary(startDate?: string | null, endDate?: string | null): Observable<EvertimeSummary> {
    const params = new HttpParams()
      .set('FromDate', startDate ?? DEFAULT_DASHBOARD_FROM_DATE)
      .set('ToDate', endDate ?? DEFAULT_DASHBOARD_TO_DATE);

    return this.http.get<EvertimeDashboardApiResponse>(`${this.apiBase}/dashboard/evertime-data`, { params }).pipe(
      map(response => this.normalizeEvertimeDashboardData(response.data)),
    );
  }

  private normalizeEvertimeDashboardData(data: EvertimeDashboardData): EvertimeSummary {
    const uniqueContractorsWorked = this.normalizeContractorsWorkedDetails(data.uniqueContractorsWorked);
    const topConsultants = (data.top10ConsultantsByGrossProfit ?? []).map(row => ({
      name: row.consultantName,
      revenue: Number(row.salesRevenue ?? 0),
      grossProfit: Number(row.grossProfit ?? 0),
      currencyCode: 'GBP',
    }));

    const recruiterBreakdown = topConsultants
      .filter(row => row.name && row.revenue > 0)
      .map(row => ({
        name: row.name,
        revenue: row.revenue,
        currencyCode: row.currencyCode,
      }));

    const revenuePerRecruiter = this.readNullableNumber(data.revenuePerRecruiter);
    const totalRevenue = recruiterBreakdown.reduce((sum, row) => sum + row.revenue, 0);
    const revenuePerConsultant = revenuePerRecruiter == null
      ? null
      : {
          totalRevenue,
          recruiterCount: recruiterBreakdown.length,
          revenuePerRecruiter,
          currencyCode: 'GBP',
          breakdown: recruiterBreakdown,
        };

    return {
      hasData: true,
      contractorsWorked: uniqueContractorsWorked.length || Number(data.contractorsWorked ?? 0),
      averageContractorMarginPct: this.readNullableNumber(data.averageContractorMarginPercent),
      clientsBilled: Number(data.numberOfClientsBilled ?? 0),
      topClientsByGrossProfit: [],
      topConsultantsByGrossProfit: topConsultants,
      placementRevenueByMonth: [],
      gpMarginTrendByMonth: [],
      contractorsWorkedTrend: (data.contractorsWorkedMonthly ?? []).map((row, index) => ({
        period: this.monthPeriodFromLabel(row.monthLabel, index),
        periodLabel: row.monthLabel,
        consultantName: 'All Consultants',
        contractorsWorked: Number(row.contractorsWorked ?? 0),
      })),
      contractorsWorkedDetails: this.normalizeContractorsWorkedDetails(
        data.uniqueContractorsWorked ?? data.contractorsWorkedDetails ??
        data.contractorsWorkedDetail ?? data.contractorWorkedDetails
      ),
      contractorsWorkedMonthlyDetails: this.normalizeContractorsWorkedDetails(
        data.contractorsWorkedMonthlySummary
      ),
      consultantLeaderboard: (data.consultantLeaderboard ?? []).map(row => ({
        consultant: row.consultantName,
        revenue: Number(row.revenue ?? 0),
        grossProfit: Number(row.grossProfit ?? 0),
        timesheets: Number(row.timesheets ?? 0),
        avgMargin: this.readNullableNumber(row.avgMarginPercent),
        commissionPaid: 0,
        currencyCode: 'GBP',
      })),
      consultantMonthlyPerformance: this.normalizeConsultantMonthlyPerformance(
        data.consultantMonthlyDrilldown ?? data.consultantMonthlyPerformance ?? data.consultantPerformanceMonthly ??
        data.consultantLeaderboardMonthly ?? data.consultantMonthlyBreakdown
      ),
      consultantInvoiceTimesheetDetails: this.normalizeConsultantInvoiceTimesheetDetails(
        data.consultantInvoiceTimesheetDetails
      ),
      clientPerformance: [],
      clientRevenueTrend: [],
      revenuePerConsultant,
    };
  }

  private monthPeriodFromLabel(label: string, fallbackIndex: number): string {
    const parsed = new Date(`1 ${label}`);
    if (Number.isNaN(parsed.getTime())) return String(fallbackIndex).padStart(2, '0');
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  }

  private normalizeEvertimeSummary(raw: Record<string, unknown>): EvertimeSummary {
    const revenuePerConsultantRaw = raw['revenuePerConsultant'] ?? raw['RevenuePerConsultant'];
    const topConsultantsByGrossProfit = this.normalizeGpRows(raw['topConsultantsByGrossProfit'] ?? raw['TopConsultantsByGrossProfit']);
    let revenuePerConsultant = this.normalizeRevenuePerConsultant(revenuePerConsultantRaw);
    if (!revenuePerConsultant) {
      revenuePerConsultant = this.buildRevenuePerConsultantFromTopConsultants(topConsultantsByGrossProfit);
    }

    return {
      hasData: Boolean(raw['hasData'] ?? raw['HasData']),
      contractorsWorked: Number(raw['contractorsWorked'] ?? raw['ContractorsWorked'] ?? 0),
      averageContractorMarginPct: this.readNullableNumber(raw['averageContractorMarginPct'] ?? raw['AverageContractorMarginPct']),
      clientsBilled: Number(raw['clientsBilled'] ?? raw['ClientsBilled'] ?? 0),
      topClientsByGrossProfit: this.normalizeGpRows(raw['topClientsByGrossProfit'] ?? raw['TopClientsByGrossProfit']),
      topConsultantsByGrossProfit,
      placementRevenueByMonth: (raw['placementRevenueByMonth'] ?? raw['PlacementRevenueByMonth'] ?? []) as EvertimePlacementRevenueRow[],
      gpMarginTrendByMonth: this.normalizeGpMarginTrend(raw['gpMarginTrendByMonth'] ?? raw['GpMarginTrendByMonth']),
      contractorsWorkedTrend: this.normalizeContractorTrend(raw['contractorsWorkedTrend'] ?? raw['ContractorsWorkedTrend']),
      contractorsWorkedDetails: this.normalizeContractorsWorkedDetails(
        raw['uniqueContractorsWorked'] ?? raw['UniqueContractorsWorked'] ??
        raw['contractorsWorkedDetails'] ?? raw['ContractorsWorkedDetails'] ??
        raw['contractorsWorkedDetail'] ?? raw['ContractorsWorkedDetail'] ??
        raw['contractorWorkedDetails'] ?? raw['ContractorWorkedDetails']
      ),
      contractorsWorkedMonthlyDetails: this.normalizeContractorsWorkedDetails(
        raw['contractorsWorkedMonthlySummary'] ?? raw['ContractorsWorkedMonthlySummary']
      ),
      consultantLeaderboard: this.normalizeConsultantLeaderboard(raw['consultantLeaderboard'] ?? raw['ConsultantLeaderboard']),
      consultantMonthlyPerformance: this.normalizeConsultantMonthlyPerformance(
        raw['consultantMonthlyDrilldown'] ?? raw['ConsultantMonthlyDrilldown'] ??
        raw['consultantMonthlyPerformance'] ?? raw['ConsultantMonthlyPerformance'] ??
        raw['consultantPerformanceMonthly'] ?? raw['ConsultantPerformanceMonthly'] ??
        raw['consultantLeaderboardMonthly'] ?? raw['ConsultantLeaderboardMonthly'] ??
        raw['consultantMonthlyBreakdown'] ?? raw['ConsultantMonthlyBreakdown']
      ),
      consultantInvoiceTimesheetDetails: this.normalizeConsultantInvoiceTimesheetDetails(
        raw['consultantInvoiceTimesheetDetails'] ?? raw['ConsultantInvoiceTimesheetDetails']
      ),
      clientPerformance: this.normalizeClientPerformance(raw['clientPerformance'] ?? raw['ClientPerformance']),
      clientRevenueTrend: this.normalizeClientRevenueTrend(raw['clientRevenueTrend'] ?? raw['ClientRevenueTrend']),
      revenuePerConsultant,
    };
  }

  private normalizeContractorTrend(raw: unknown): EvertimeContractorTrendPoint[] {
    if (!Array.isArray(raw)) return [];

    return raw
      .map(item => {
        if (!item || typeof item !== 'object') return null;
        const row = item as Record<string, unknown>;
        const period = this.readNullableString(row['period'] ?? row['Period']);
        if (!period) return null;

        return {
          period,
          periodLabel: this.readNullableString(row['periodLabel'] ?? row['PeriodLabel']) ?? period,
          consultantName: this.readNullableString(row['consultantName'] ?? row['ConsultantName']) ?? 'Unassigned',
          contractorsWorked: Number(row['contractorsWorked'] ?? row['ContractorsWorked'] ?? 0),
        };
      })
      .filter((row): row is EvertimeContractorTrendPoint => row !== null);
  }

  private normalizeContractorsWorkedDetails(raw: unknown): EvertimeContractorWorkedDetailRow[] {
    if (!Array.isArray(raw)) return [];

    return raw
      .map(item => {
        if (!item || typeof item !== 'object') return null;
        const row = item as Record<string, unknown>;
        const candidateName = this.readNullableString(row['candidateName'] ?? row['CandidateName']);
        if (!candidateName) return null;

        return {
          monthLabel: this.readNullableString(row['monthLabel'] ?? row['MonthLabel']) ?? '',
          candidateName,
          firstWorkDate: this.readDateOnlyString(
            row['firstWorkedDate'] ?? row['FirstWorkedDate'] ??
            row['firstWorkDate'] ?? row['FirstWorkDate'] ?? row['firstWorkdate'] ?? row['FirstWorkdate']
          ) ?? '',
          lastWorkDate: this.readDateOnlyString(
            row['lastWorkedDate'] ?? row['LastWorkedDate'] ??
            row['lastWorkDate'] ?? row['LastWorkDate'] ?? row['lastWorkdate'] ?? row['LastWorkdate']
          ) ?? '',
          daysWorked: Number(row['daysWorked'] ?? row['DaysWorked'] ?? 0),
        };
      })
      .filter((row): row is EvertimeContractorWorkedDetailRow => row !== null);
  }

  private normalizeConsultantLeaderboard(raw: unknown): EvertimeConsultantLeaderboardRow[] {
    if (!Array.isArray(raw)) return [];

    return raw
      .map(item => {
        if (!item || typeof item !== 'object') return null;
        const row = item as Record<string, unknown>;
        const consultant = this.readNullableString(row['consultant'] ?? row['Consultant']);
        if (!consultant) return null;

        return {
          consultant,
          revenue: Number(row['revenue'] ?? row['Revenue'] ?? 0),
          grossProfit: Number(row['grossProfit'] ?? row['GrossProfit'] ?? 0),
          timesheets: Number(
            row['timesheets'] ?? row['Timesheets'] ??
            row['placements'] ?? row['Placements'] ?? 0
          ),
          avgMargin: this.readNullableNumber(row['avgMargin'] ?? row['AvgMargin']),
          commissionPaid: Number(row['commissionPaid'] ?? row['CommissionPaid'] ?? 0),
          currencyCode: this.readNullableString(row['currencyCode'] ?? row['CurrencyCode']),
        };
      })
      .filter((row): row is EvertimeConsultantLeaderboardRow => row !== null);
  }

  private normalizeConsultantMonthlyPerformance(raw: unknown): EvertimeConsultantMonthlyPerformanceRow[] {
    if (!Array.isArray(raw)) return [];

    return raw
      .map(item => {
        if (!item || typeof item !== 'object') return null;
        const row = item as Record<string, unknown>;
        const consultant = this.readNullableString(
          row['consultant'] ?? row['Consultant'] ?? row['consultantName'] ?? row['ConsultantName']
        );
        const monthLabel = this.readNullableString(row['monthLabel'] ?? row['MonthLabel']);
        if (!consultant || !monthLabel) return null;

        return {
          monthLabel,
          consultant,
          invoiceCount: Number(row['invoiceCount'] ?? row['InvoiceCount'] ?? 0),
          revenue: Number(row['revenue'] ?? row['Revenue'] ?? 0),
          purchaseCost: Number(row['purchaseCost'] ?? row['PurchaseCost'] ?? 0),
          grossProfit: Number(row['grossProfit'] ?? row['GrossProfit'] ?? 0),
          timesheets: Number(row['timesheets'] ?? row['Timesheets'] ?? 0),
          avgMargin: this.readNullableNumber(
            row['avgMargin'] ?? row['AvgMargin'] ?? row['avgMarginPercent'] ?? row['AvgMarginPercent']
          ),
          currencyCode: this.readNullableString(row['currencyCode'] ?? row['CurrencyCode']),
        };
      })
      .filter((row): row is EvertimeConsultantMonthlyPerformanceRow => row !== null);
  }

  private normalizeConsultantInvoiceTimesheetDetails(raw: unknown): EvertimeConsultantInvoiceTimesheetDetailRow[] {
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
      .map(row => ({
        ...row,
        consultantId: this.readNullableString(row['consultantId'] ?? row['ConsultantId']) ?? '',
        consultantName: this.readNullableString(row['consultantName'] ?? row['ConsultantName']) ?? '',
        monthLabel: this.readNullableString(row['monthLabel'] ?? row['MonthLabel']) ?? '',
        invoiceDate: this.readDateOnlyString(row['invoiceDate'] ?? row['InvoiceDate']) ?? '',
        periodEndDate: this.readDateOnlyString(row['periodEndDate'] ?? row['PeriodEndDate']) ?? '',
        reconcileDate: this.readDateOnlyString(row['reconcileDate'] ?? row['ReconcileDate']) ?? '',
      }));
  }

  private normalizeClientPerformance(raw: unknown): EvertimeClientPerformanceRow[] {
    if (!Array.isArray(raw)) return [];

    return raw
      .map(item => {
        if (!item || typeof item !== 'object') return null;
        const row = item as Record<string, unknown>;
        const client = this.readNullableString(row['client'] ?? row['Client']);
        if (!client) return null;

        return {
          client,
          revenue: Number(row['revenue'] ?? row['Revenue'] ?? 0),
          grossProfit: Number(row['grossProfit'] ?? row['GrossProfit'] ?? 0),
          gpPct: this.readNullableNumber(row['gpPct'] ?? row['GpPct']),
          placements: Number(row['placements'] ?? row['Placements'] ?? 0),
          contractors: Number(row['contractors'] ?? row['Contractors'] ?? 0),
          outstandingBalance: Number(row['outstandingBalance'] ?? row['OutstandingBalance'] ?? 0),
          currencyCode: this.readNullableString(row['currencyCode'] ?? row['CurrencyCode']),
        };
      })
      .filter((row): row is EvertimeClientPerformanceRow => row !== null);
  }

  private normalizeClientRevenueTrend(raw: unknown): EvertimeClientRevenueTrendPoint[] {
    if (!Array.isArray(raw)) return [];

    return raw
      .map(item => {
        if (!item || typeof item !== 'object') return null;
        const row = item as Record<string, unknown>;
        const month = this.readNullableString(row['month'] ?? row['Month']);
        const client = this.readNullableString(row['client'] ?? row['Client']);
        if (!month || !client) return null;

        return {
          month,
          monthLabel: this.readNullableString(row['monthLabel'] ?? row['MonthLabel']) ?? month,
          client,
          revenue: Number(row['revenue'] ?? row['Revenue'] ?? 0),
          currencyCode: this.readNullableString(row['currencyCode'] ?? row['CurrencyCode']),
        };
      })
      .filter((row): row is EvertimeClientRevenueTrendPoint => row !== null);
  }

  private normalizeChartData(raw: Record<string, unknown>): ChartData {
    return {
      revenueByClient: (raw['revenueByClient'] ?? raw['RevenueByClient'] ?? []) as RevenueByClientItem[],
      monthlyRevenue: (raw['monthlyRevenue'] ?? raw['MonthlyRevenue'] ?? []) as MonthlyRevenueItem[],
      revenueByPeriod: (raw['revenueByPeriod'] ?? raw['RevenueByPeriod'] ?? []) as RevenuePeriodItem[],
      invoicesByStatus: (raw['invoicesByStatus'] ?? raw['InvoicesByStatus'] ?? []) as InvoiceStatusItem[],
      recentInvoices: (raw['recentInvoices'] ?? raw['RecentInvoices'] ?? []) as InvoiceTableRow[],
      recentContacts: (raw['recentContacts'] ?? raw['RecentContacts'] ?? []) as ContactTableRow[],
      recentPayments: (raw['recentPayments'] ?? raw['RecentPayments'] ?? []) as PaymentTableRow[],
      cashFlowTrend: (raw['cashFlowTrend'] ?? raw['CashFlowTrend'] ?? []) as CashFlowTrendItem[],
      financialOverview: (raw['financialOverview'] ?? raw['FinancialOverview'] ?? []) as FinancialOverviewPoint[],
      agedDebtBuckets: this.normalizeAgedDebtBuckets(raw['agedDebtBuckets'] ?? raw['AgedDebtBuckets']),
      creditControlClients: this.normalizeCreditControlClients(raw['creditControlClients'] ?? raw['CreditControlClients']),
    };
  }

  private normalizeAgedDebtBuckets(raw: unknown): AgedDebtBucketItem[] {
    if (!Array.isArray(raw)) return [];

    return raw
      .map(item => {
        if (!item || typeof item !== 'object') return null;
        const row = item as Record<string, unknown>;
        const bucket = this.readNullableString(row['bucket'] ?? row['Bucket']);
        if (!bucket) return null;

        return {
          bucket,
          amount: Number(row['amount'] ?? row['Amount'] ?? 0),
        };
      })
      .filter((row): row is AgedDebtBucketItem => row !== null);
  }

  private normalizeCreditControlClients(raw: unknown): CreditControlClientRow[] {
    if (!Array.isArray(raw)) return [];

    return raw
      .map(item => {
        if (!item || typeof item !== 'object') return null;
        const row = item as Record<string, unknown>;
        const clientName = this.readNullableString(row['clientName'] ?? row['ClientName']);
        if (!clientName) return null;

        return {
          clientName,
          totalOutstanding: Number(row['totalOutstanding'] ?? row['TotalOutstanding'] ?? 0),
          bucket0To30: Number(row['bucket0To30'] ?? row['Bucket0To30'] ?? 0),
          bucket31To60: Number(row['bucket31To60'] ?? row['Bucket31To60'] ?? 0),
          bucket61To90: Number(row['bucket61To90'] ?? row['Bucket61To90'] ?? 0),
          bucket90Plus: Number(row['bucket90Plus'] ?? row['Bucket90Plus'] ?? 0),
          dso: this.readNullableNumber(row['dso'] ?? row['Dso']),
        };
      })
      .filter((row): row is CreditControlClientRow => row !== null);
  }

  private normalizeGpMarginTrend(raw: unknown): EvertimeGpMarginTrendPoint[] {
    if (!Array.isArray(raw)) return [];

    return raw.flatMap(item => {
        if (!item || typeof item !== 'object') return [];
        const row = item as Record<string, unknown>;
        const month = this.readNullableString(row['month'] ?? row['Month']);
        if (!month) return [];
        const monthLabel = this.readNullableString(row['monthLabel'] ?? row['MonthLabel']) ?? month;

        return [
          {
            month,
            monthLabel,
            category: 'Permanent',
            gpmPct: this.readNullableNumber(row['permGpPct'] ?? row['PermGpPct']),
          },
          {
            month,
            monthLabel,
            category: 'Temporary',
            gpmPct: this.readNullableNumber(row['tempGpPct'] ?? row['TempGpPct']),
          },
        ];
      });
  }

  private buildRevenuePerConsultantFromTopConsultants(
    rows: EvertimeGpRow[]
  ): EvertimeRevenuePerConsultantSummary | null {
    const breakdown = rows
      .filter(row => row.name && Number(row.revenue) > 0)
      .map(row => ({
        name: row.name,
        revenue: Number(row.revenue),
        currencyCode: row.currencyCode ?? null,
      }));

    if (!breakdown.length) return null;

    const totalRevenue = breakdown.reduce((sum, row) => sum + row.revenue, 0);
    const recruiterCount = breakdown.length;

    return {
      totalRevenue,
      recruiterCount,
      revenuePerRecruiter: recruiterCount > 0 ? totalRevenue / recruiterCount : totalRevenue,
      currencyCode: breakdown[0]?.currencyCode ?? null,
      breakdown,
    };
  }

  private normalizeRevenuePerConsultant(raw: unknown): EvertimeRevenuePerConsultantSummary | null {
    if (!raw || typeof raw !== 'object') return null;

    const source = raw as Record<string, unknown>;
    const breakdown = this.normalizeConsultantBreakdown(source['breakdown'] ?? source['Breakdown']);
    if (!breakdown.length) return null;

    const totalRevenue = breakdown.reduce((sum, row) => sum + row.revenue, 0);
    const recruiterCount = breakdown.length;
    const revenuePerRecruiter = this.readNullableNumber(source['revenuePerRecruiter'] ?? source['RevenuePerRecruiter'])
      ?? (recruiterCount > 0 ? totalRevenue / recruiterCount : totalRevenue);

    return {
      totalRevenue,
      recruiterCount,
      revenuePerRecruiter,
      currencyCode: this.readNullableString(source['currencyCode'] ?? source['CurrencyCode']) ?? breakdown[0]?.currencyCode ?? null,
      breakdown,
    };
  }

  private normalizeConsultantBreakdown(raw: unknown): EvertimeRevenuePerConsultantRow[] {
    if (!Array.isArray(raw)) return [];

    return raw
      .map(item => {
        if (!item || typeof item !== 'object') return null;
        const row = item as Record<string, unknown>;
        const revenue = this.readNullableNumber(row['revenue'] ?? row['Revenue']);
        const name = this.readNullableString(row['name'] ?? row['Name']);
        if (revenue == null || revenue <= 0 || !name) return null;

        return {
          name,
          revenue,
          currencyCode: this.readNullableString(row['currencyCode'] ?? row['CurrencyCode']),
        };
      })
      .filter((row): row is EvertimeRevenuePerConsultantRow => row !== null);
  }

  private normalizeGpRows(raw: unknown): EvertimeGpRow[] {
    if (!Array.isArray(raw)) return [];

    return raw
      .map(item => {
        if (!item || typeof item !== 'object') return null;
        const row = item as Record<string, unknown>;
        return {
          name: this.readNullableString(row['name'] ?? row['Name']) ?? 'Unknown',
          revenue: Number(row['revenue'] ?? row['Revenue'] ?? 0),
          grossProfit: Number(row['grossProfit'] ?? row['GrossProfit'] ?? 0),
          currencyCode: this.readNullableString(row['currencyCode'] ?? row['CurrencyCode']),
        };
      })
      .filter((row): row is EvertimeGpRow => row !== null);
  }

  private readNullableNumber(value: unknown): number | null {
    if (value == null || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private readNullableString(value: unknown): string | null {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  }

  private readDateOnlyString(value: unknown): string | null {
    const dateTime = this.readNullableString(value);
    if (!dateTime) return null;
    return dateTime.split('T', 1)[0];
  }


  getSyncStatus(): Observable<SyncStatusResponse> {
    return this.http.get<SyncStatusResponse>(`${this.apiBase}/sync/status`);
  }

  triggerSync(): Observable<void> {
    return this.http.post<void>(`${this.apiBase}/sync/trigger`, {});
  }
}


